<!--
	Ported from analytics-design/src/components/AnalysisDashboard.tsx, then rewired to real data -
	see the component's own comment for what changed (the KPI row is now the standard 4 metrics
	instead of 12 tab-specific cards, and "Reach x Sentiment per Brand" was dropped for lack of a
	backend equivalent).

	SETTE redesign (CLIPPING-598): token-by-token restyle, "Reach" -> "Visibility" in all copy.
	Правило А drives most of the color changes here: Reach/Visibility carries no sign, so its
	per-row numbers/bars are no longer green (brand/media-type rows keep their own identity
	color instead - the same color they already use on the Mentions tab); only the Impact tab
	colors by sign (toneClasses/signTone), everywhere else bars are plain blue or identity-colored.
	The metric switch keeps its own panel for now (not moved into FilterBar's "leading" slot,
	which is a cross-screen structural change out of scope for this pass) but drops the
	per-tab color (blue/emerald/purple) for the standard neutral SegmentedControl look.
-->
<div class="space-y-4">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="4" [chartCount]="2"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetch()"></app-analytics-error-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- 1. METRIC SEGMENTED CONTROL -->
	<div class="bg-surface p-3 rounded-2xl border border-line flex items-center">
		<div class="inline-flex bg-surface-gray p-1 rounded-full">
			<button
				type="button"
				(click)="setMetric('Mentions')"
				class="px-5 h-8 rounded-full text-[13px] font-medium transition-colors cursor-pointer"
				[class]="metric === 'Mentions' ? 'bg-surface text-ink shadow-[0_1px_3px_rgba(28,29,33,0.10)]' : 'text-ink-muted hover:text-ink'">
				{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}
			</button>
			<button
				type="button"
				(click)="setMetric('Reach')"
				class="px-5 h-8 rounded-full text-[13px] font-medium transition-colors cursor-pointer"
				[class]="metric === 'Reach' ? 'bg-surface text-ink shadow-[0_1px_3px_rgba(28,29,33,0.10)]' : 'text-ink-muted hover:text-ink'">
				{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}
			</button>
			<button
				type="button"
				(click)="setMetric('Impact')"
				class="px-5 h-8 rounded-full text-[13px] font-medium transition-colors cursor-pointer"
				[class]="metric === 'Impact' ? 'bg-surface text-ink shadow-[0_1px_3px_rgba(28,29,33,0.10)]' : 'text-ink-muted hover:text-ink'">
				{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}
			</button>
		</div>
	</div>

	<!-- 3. KPI ROW -->
	<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.MENTIONS' | translate" infoKey="ANALYTICS_INFO.COMMON.MENTIONS" [value]="mentionsKpi.value" [trendUp]="mentionsKpi.trendUp" [deltaLabel]="mentionsKpi.deltaLabel" [subLabel]="'ANALYTICS_GLOSSARY.SELECTED_PERIOD' | translate" [sampleSize]="data?.mentions?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX' | translate" infoKey="ANALYTICS_INFO.COMMON.VISIBILITY" [value]="avgReachKpi.value" [trendUp]="avgReachKpi.trendUp" [deltaLabel]="avgReachKpi.deltaLabel" [subLabel]="'· ' + ('ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate)" [sampleSize]="data?.avgReach?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE' | translate" infoKey="ANALYTICS_INFO.COMMON.IMPACT" [value]="avgImpactKpi.value" [trendUp]="avgImpactKpi.trendUp" [deltaLabel]="avgImpactKpi.deltaLabel" [subLabel]="'· ' + ('ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate)" [sampleSize]="data?.avgImpact?.sampleSize"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.SHARE_OF_VOICE' | translate" infoKey="ANALYTICS_INFO.COMMON.SHARE_OF_VOICE" [value]="shareOfVoiceKpi.value" [trendUp]="shareOfVoiceKpi.trendUp" [deltaLabel]="shareOfVoiceKpi.deltaLabel" [sampleSize]="data?.shareOfVoice?.sampleSize"></app-kpi-tile>
	</div>

	<!-- 4. SHARED ROW 1: [1. Trend - 2 cols] [2. By Brand - 1 col] -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- WIDGET 1: Trend -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between lg:col-span-2 min-h-[330px]">
			<div>
				<div class="flex items-center justify-between mb-1">
					<div class="flex items-center gap-2">
						<h3 class="section-title flex items-center gap-1">1. {{ 'ANALYTICS_COVERAGE_ANALYSIS.TREND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.TREND"></app-info-icon></h3>
						<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_COVERAGE_ANALYSIS.METRIC_OVER_PERIOD' | translate : { metric: metricLabel() } }}</span>
					</div>
					<div class="flex items-center gap-1.5 font-medium text-ink caption">
						<span class="w-2.5 h-2.5 rounded-full bg-blue"></span>
						<span>{{ 'ANALYTICS_COVERAGE_ANALYSIS.CURRENT_PERIOD' | translate }}</span>
					</div>
				</div>
			</div>

			<div class="w-full h-[220px] mt-2">
				<app-chart type="line" [data]="trendChartData" [options]="trendChartOptions" height="220px"></app-chart>
			</div>
			@if (trendSampleSize !== undefined) {
			<app-sample-size-note class="block mt-1" [count]="trendSampleSize"></app-sample-size-note>
			}
		</div>

		<!-- WIDGET 2: By Brand -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[330px]">
			<div>
				<div class="flex items-center justify-between mb-1">
					<h3 class="section-title flex items-center gap-1">2. {{ 'ANALYTICS_COVERAGE_ANALYSIS.BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.BY_BRAND"></app-info-icon></h3>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.BRANDS_COUNT' | translate : { count: sortedBrandList.length } }}</span>
				</div>
			</div>

			@if (metric === 'Mentions') {
			<div class="space-y-2.5 text-xs flex-1 flex flex-col justify-center">
				@for (b of sortedBrandList; track b.brand) {
				<div class="space-y-1">
					<div class="flex justify-between items-center text-[11px]">
						<span class="font-medium text-ink">{{ b.brand }}</span>
						<div class="flex items-center gap-2">
							<span class="font-medium text-ink tabular-nums">{{ fmtInt(b.volume) }}</span>
							<span class="caption text-ink-subtle font-normal">({{ b.sov }})</span>
						</div>
					</div>
					<div class="w-full bg-neutral-tint rounded-full h-2.5 overflow-hidden">
						<div class="h-full rounded-full transition-all duration-300" [style.width.%]="(b.volume / maxBrandVolume) * 100" [style.background-color]="b.color"></div>
					</div>
				</div>
				}
			</div>
			}

			@if (metric === 'Reach') {
			<div class="space-y-2.5 text-xs flex-1 flex flex-col justify-center">
				@for (b of sortedBrandList; track b.brand) {
				<div class="space-y-1">
					<div class="flex justify-between items-center text-[11px]">
						<span class="font-medium text-ink">{{ b.brand }}</span>
						<span class="font-medium text-ink tabular-nums">{{ fmt1(b.reach) }} <span class="caption text-ink-subtle font-normal">pts</span></span>
					</div>
					<div class="w-full bg-neutral-tint rounded-full h-2.5 overflow-hidden">
						<div class="h-full rounded-full transition-all duration-300" [style.width.%]="b.reach" [style.background-color]="b.color"></div>
					</div>
				</div>
				}
			</div>
			}

			@if (metric === 'Impact') {
			<div class="space-y-2 text-xs flex-1 flex flex-col justify-center">
				@for (b of sortedBrandList; track b.brand) {
				<div class="grid grid-cols-12 items-center gap-1">
					<span class="col-span-3 font-medium text-ink text-[11px] truncate">{{ b.brand }}</span>
					<div class="col-span-6 grid grid-cols-2 h-3 bg-neutral-tint rounded-full overflow-hidden">
						<div class="flex justify-end border-r border-line-strong">
							@if (b.impactTone === 'negative') {
							<div class="h-full bg-negative rounded-l-full transition-all" [style.width.%]="divergingPercent(b.impact)"></div>
							}
						</div>
						<div class="flex justify-start">
							@if (b.impactTone === 'positive') {
							<div class="h-full bg-positive rounded-r-full transition-all" [style.width.%]="divergingPercent(b.impact)"></div>
							}
						</div>
					</div>
					<div class="col-span-3 text-right">
						<span class="font-medium px-1.5 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(b.impactTone)">{{ b.impactStr }}</span>
					</div>
				</div>
				}
			</div>
			}

			@if (!loading && sortedBrandList.length === 0) {
			<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_BRAND_DATA' | translate }}</div>
			}

			<div class="flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				<span>{{ brandFooterLine }}</span>
			</div>
		</div>
	</div>

	<!-- 5. SHARED ROW 2: [3. By Media Type - 1 col] [4. Top Media - 2 cols] -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- WIDGET 3: By Media Type -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[320px]">
			<div>
				<div class="flex items-center justify-between mb-1">
					<h3 class="section-title flex items-center gap-1">3. {{ 'ANALYTICS_COVERAGE_ANALYSIS.BY_MEDIA_TYPE_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.BY_MEDIA_TYPE"></app-info-icon></h3>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.METRIC_VIEW' | translate : { metric: metricLabel() } }}</span>
				</div>
			</div>

			@if (metric === 'Mentions') {
			<div class="relative w-full h-[125px] flex items-center justify-center">
				<app-chart type="doughnut" [data]="mediaTypeChartData" [options]="mediaTypeChartOptions" height="125px"></app-chart>
				<div class="absolute text-center pointer-events-none">
					<div class="text-xs font-medium text-ink tabular-nums">{{ mediaTypeTotal }}</div>
					<div class="overline-label text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.TOTAL_LABEL' | translate }}</div>
				</div>
			</div>

			@if (mediaTypeTotal > 0) {
			<app-sample-size-note class="block text-center" [count]="mediaTypeTotal"></app-sample-size-note>
			}

			<div class="space-y-1 text-[10px] border-t border-line pt-1.5">
				@for (m of mediaTypeData; track m.name) {
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-1.5">
						<span class="w-2 h-2 rounded-full" [style.background-color]="m.color"></span>
						<span class="text-ink-muted font-medium">{{ m.name }}</span>
					</div>
					<span class="font-medium text-ink tabular-nums">{{ m.percentage }} <span class="text-ink-subtle font-normal">({{ fmtInt(m.volume) }})</span></span>
				</div>
				}
			</div>
			}

			@if (metric === 'Reach') {
			<div class="space-y-2.5 text-[10px] flex-1 flex flex-col justify-center">
				@for (m of sortedMediaTypeData; track m.name) {
				<div class="space-y-0.5">
					<div class="flex items-center justify-between">
						<span class="text-ink-muted font-medium">{{ m.name }}</span>
						<span class="font-medium text-ink text-[11px] tabular-nums">{{ fmt1(m.reach) }} pts</span>
					</div>
					<div class="w-full bg-neutral-tint rounded-full h-2.5 overflow-hidden">
						<div class="h-full rounded-full transition-all" [style.width.%]="m.reach" [style.background-color]="m.color"></div>
					</div>
				</div>
				}
			</div>
			}

			@if (metric === 'Impact') {
			<div class="space-y-2 text-[10px] flex-1 flex flex-col justify-center">
				@for (m of sortedMediaTypeData; track m.name) {
				<div class="grid grid-cols-12 items-center gap-1">
					<span class="col-span-4 font-medium text-ink-muted text-[10px] truncate">{{ m.name }}</span>
					<div class="col-span-5 grid grid-cols-2 h-3 bg-neutral-tint rounded-full overflow-hidden">
						<div class="flex justify-end border-r border-line-strong">
							@if (m.impactTone === 'negative') {
							<div class="h-full bg-negative rounded-l-full" [style.width.%]="divergingPercent(m.impact)"></div>
							}
						</div>
						<div class="flex justify-start">
							@if (m.impactTone === 'positive') {
							<div class="h-full bg-positive rounded-r-full" [style.width.%]="divergingPercent(m.impact)"></div>
							}
						</div>
					</div>
					<div class="col-span-3 text-right">
						<span class="font-medium px-1 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(m.impactTone)">{{ m.impactStr }}</span>
					</div>
				</div>
				}
			</div>
			}

			@if (!loading && mediaTypeData.length === 0) {
			<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_MEDIA_TYPE_DATA' | translate }}</div>
			}

			<div class="flex items-center justify-between caption text-ink-subtle font-normal pt-2 border-t border-line">
				<span>{{ mediaTypeFooterLine }}</span>
			</div>
		</div>

		<!-- WIDGET 4: Top Media Outlets -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between lg:col-span-2 min-h-[320px]">
			<div>
				<div class="flex items-center justify-between mb-1">
					<div class="flex items-center gap-2">
						<h3 class="section-title flex items-center gap-1">4. {{ 'ANALYTICS_COVERAGE_ANALYSIS.TOP_MEDIA_OUTLETS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.TOP_MEDIA_OUTLETS"></app-info-icon></h3>
						<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_COVERAGE_ANALYSIS.RANKED_BY' | translate : { metric: metricLabel() } }}</span>
					</div>
					<button type="button" (click)="onNavigate('media')" class="text-blue hover:underline font-medium text-[11px] flex items-center gap-0.5 cursor-pointer">
						<span>{{ 'ANALYTICS_COVERAGE_ANALYSIS.VIEW_ALL_OUTLETS' | translate }}</span>
						<mat-icon class="!w-3 !h-3 !text-xs">arrow_forward</mat-icon>
					</button>
				</div>
			</div>

			<div class="overflow-x-auto">
				<table class="w-full text-left text-xs">
					<thead>
						<tr class="border-b border-line overline-label text-ink-subtle">
							<th class="py-1 px-1.5 w-8">#</th>
							<th class="py-1 px-1.5">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_OUTLET_NAME' | translate }}</th>
							<th class="py-1 px-1.5">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_CHANNEL' | translate }}</th>
							<th class="py-1 px-1.5 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
							<th class="py-1 px-1.5 text-right">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_AVG_VISIBILITY' | translate }}</th>
							<th class="py-1 px-1.5 text-right">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_AVG_IMPACT' | translate }}</th>
						</tr>
					</thead>
					<tbody>
						@for (m of sortedTopMediaTop6; track m.name; let idx = $index) {
						<tr class="border-t border-line hover:bg-surface-blue transition-colors">
							<td class="py-2 px-1.5 font-medium text-ink-subtle text-[11px]">{{ idx + 1 }}</td>
							<td class="py-2 px-1.5 font-medium text-ink text-[11px]">{{ m.name }}</td>
							<td class="py-2 px-1.5">
								<span class="text-[10px] text-ink-muted bg-surface-gray px-1.5 py-0.5 rounded-full font-medium">{{ m.type }}</span>
							</td>
							<td class="py-2 px-1.5 text-right font-medium text-ink text-[11px] tabular-nums">{{ fmtInt(m.mentions) }}</td>
							<td class="py-2 px-1.5 text-right">
								<span class="font-medium text-ink text-[11px] bg-surface-gray px-1.5 py-0.5 rounded-full tabular-nums">{{ m.reach }}</span>
							</td>
							<td class="py-2 px-1.5 text-right">
								<span class="font-medium text-[11px] px-1.5 py-0.5 rounded-full tabular-nums" [class]="toneClasses(m.impactTone)">{{ m.impactStr }}</span>
							</td>
						</tr>
						}
						@if (!loading && sortedTopMediaTop6.length === 0) {
						<tr><td colspan="6" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_OUTLET_DATA' | translate }}</td></tr>
						}
					</tbody>
				</table>
			</div>
		</div>
	</div>

	<!-- 6. SHARED ROW 3: [5. Industry Topics] [6. Corporate Topics] [7. Top Authors] -->
	<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
		<!-- WIDGET 5: Industry Topics -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[300px]">
			<div class="flex items-center justify-between mb-1">
				<h3 class="section-title flex items-center gap-1">5. {{ 'ANALYTICS_COVERAGE_ANALYSIS.INDUSTRY_TOPICS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.INDUSTRY_TOPICS"></app-info-icon></h3>
				<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.MARKET_INDUSTRY_TRENDS' | translate }}</span>
			</div>

			<div class="space-y-2 text-[10px] flex-1 flex flex-col justify-center">
				@for (t of sortedIndustryTopics; track t.name) {
				<div class="space-y-0.5">
					<div class="flex justify-between items-center">
						<span class="font-medium text-ink text-[11px]">{{ t.name }}</span>
						<div class="flex items-center gap-1.5">
							@if (metric === 'Mentions') {
							<span class="font-medium text-ink text-[11px] tabular-nums">{{ fmtInt(t.volume) }}</span>
							<span class="text-ink-subtle text-[10px]">({{ t.percentage }})</span>
							}
							@if (metric === 'Reach') {
							<span class="font-medium text-ink text-[11px] tabular-nums">{{ t.reach }} pts</span>
							}
							@if (metric === 'Impact') {
							<span class="font-medium text-[10px] px-1 py-0.2 rounded-full tabular-nums" [class]="toneClasses(t.impactTone)">{{ t.impactStr }}</span>
							}
						</div>
					</div>
					<div class="w-full bg-neutral-tint rounded-full h-2 overflow-hidden">
						<div class="h-full rounded-full transition-all" [style.width.%]="t.barWidth" [style.background-color]="topicBarColor(t)"></div>
					</div>
				</div>
				}
				@if (!loading && sortedIndustryTopics.length === 0) {
				<div class="text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_INDUSTRY_TOPIC_DATA' | translate }}</div>
				}
			</div>

			<div class="pt-2 border-t border-line">
				<p class="caption text-ink-subtle font-normal italic">{{ 'ANALYTICS_COVERAGE_ANALYSIS.TOPICS_FOOTNOTE' | translate }}</p>
			</div>
		</div>

		<!-- WIDGET 6: Corporate Topics -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[300px]">
			<div class="flex items-center justify-between mb-1">
				<h3 class="section-title flex items-center gap-1">6. {{ 'ANALYTICS_COVERAGE_ANALYSIS.CORPORATE_TOPICS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.CORPORATE_TOPICS"></app-info-icon></h3>
				<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.CORPORATE_CSR' | translate }}</span>
			</div>

			<div class="space-y-2 text-[10px] flex-1 flex flex-col justify-center">
				@for (t of sortedCorporateTopics; track t.name) {
				<div class="space-y-0.5">
					<div class="flex justify-between items-center">
						<span class="font-medium text-ink text-[11px]">{{ t.name }}</span>
						<div class="flex items-center gap-1.5">
							@if (metric === 'Mentions') {
							<span class="font-medium text-ink text-[11px] tabular-nums">{{ fmtInt(t.volume) }}</span>
							<span class="text-ink-subtle text-[10px]">({{ t.percentage }})</span>
							}
							@if (metric === 'Reach') {
							<span class="font-medium text-ink text-[11px] tabular-nums">{{ t.reach }} pts</span>
							}
							@if (metric === 'Impact') {
							<span class="font-medium text-[10px] px-1 py-0.2 rounded-full tabular-nums" [class]="toneClasses(t.impactTone)">{{ t.impactStr }}</span>
							}
						</div>
					</div>
					<div class="w-full bg-neutral-tint rounded-full h-2 overflow-hidden">
						<div class="h-full rounded-full transition-all" [style.width.%]="t.barWidth" [style.background-color]="topicBarColor(t)"></div>
					</div>
				</div>
				}
				@if (!loading && sortedCorporateTopics.length === 0) {
				<div class="text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_CORPORATE_TOPIC_DATA' | translate }}</div>
				}
			</div>

			<div class="pt-2 border-t border-line">
				<p class="caption text-ink-subtle font-normal italic">{{ 'ANALYTICS_COVERAGE_ANALYSIS.TOPICS_FOOTNOTE' | translate }}</p>
			</div>
		</div>

		<!-- WIDGET 7: Top Authors -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[300px]">
			<div class="flex items-center justify-between mb-1">
				<h3 class="section-title flex items-center gap-1">7. {{ 'ANALYTICS_COVERAGE_ANALYSIS.TOP_AUTHORS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.TOP_AUTHORS"></app-info-icon></h3>
				<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.RANKED_BY' | translate : { metric: metricLabel() } }}</span>
			</div>

			<div class="overflow-x-auto">
				<table class="w-full text-left text-xs">
					<thead>
						<tr class="border-b border-line overline-label text-ink-subtle">
							<th class="py-1 px-1">#</th>
							<th class="py-1 px-1">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_AUTHOR' | translate }}</th>
							<th class="py-1 px-1">{{ 'ANALYTICS_COVERAGE_ANALYSIS.COL_OUTLET_SHORT' | translate }}</th>
							<th class="py-1 px-1 text-right">{{ metricLabel() }}</th>
						</tr>
					</thead>
					<tbody>
						@for (a of sortedTopAuthorsTop5; track a.author; let idx = $index) {
						<tr class="border-t border-line hover:bg-surface-blue transition-colors">
							<td class="py-1.5 px-1 font-medium text-ink-subtle text-[10px]">{{ idx + 1 }}</td>
							<td class="py-1.5 px-1 font-medium text-ink text-[11px] truncate max-w-[100px]">{{ a.author }}</td>
							<td class="py-1.5 px-1 text-[10px] text-ink-muted truncate max-w-[80px]">{{ a.media }}</td>
							<td class="py-1.5 px-1 text-right font-medium text-[11px] tabular-nums">
								@if (metric === 'Mentions') { <span class="text-ink">{{ fmtInt(a.volume) }}</span> }
								@if (metric === 'Reach') { <span class="text-ink">{{ a.reach }}</span> }
								@if (metric === 'Impact') {
								<span class="px-1 py-0.2 rounded-full" [class]="toneClasses(a.impactTone)">{{ a.impactStr }}</span>
								}
							</td>
						</tr>
						}
						@if (!loading && sortedTopAuthorsTop5.length === 0) {
						<tr><td colspan="4" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_AUTHOR_DATA' | translate }}</td></tr>
						}
					</tbody>
				</table>
			</div>
		</div>
	</div>

	<!-- 7. METRIC-SPECIFIC BOTTOM BLOCK -->
	@if (metric === 'Mentions') {
	<div class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
		<!-- Sentiment Split per Brand -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between">
			<div>
				<div class="flex items-center justify-between mb-1">
					<h3 class="section-title flex items-center gap-1">8. {{ 'ANALYTICS_COVERAGE_ANALYSIS.SENTIMENT_SPLIT_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.SENTIMENT_SPLIT_PER_BRAND"></app-info-icon></h3>
					<div class="flex items-center gap-3 caption font-medium">
						<div class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-positive"></span><span class="text-ink-muted">{{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span></div>
						<div class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-neutral-bar"></span><span class="text-ink-muted">{{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span></div>
						<div class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-negative"></span><span class="text-ink-muted">{{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span></div>
					</div>
				</div>
			</div>

			<div class="space-y-2.5 text-xs py-2">
				@for (b of sentimentSplitData; track b.brand) {
				<div class="space-y-1">
					<div class="flex justify-between items-center text-[11px]">
						<span class="font-medium text-ink">{{ b.brand }}</span>
						<div class="flex items-center gap-2 caption font-medium">
							<span class="text-positive">{{ b.pos }}%</span>
							<span class="text-ink-subtle">{{ b.neu }}%</span>
							<span class="text-negative">{{ b.neg }}%</span>
						</div>
					</div>
					<div class="w-full h-3 flex gap-[2px]">
						<div [style.width.%]="b.pos" class="bg-positive h-full rounded-full" [title]="('ANALYTICS_GLOSSARY.POSITIVE' | translate) + ': ' + b.pos + '%'"></div>
						<div [style.width.%]="b.neu" class="bg-neutral-bar h-full rounded-full" [title]="('ANALYTICS_GLOSSARY.NEUTRAL' | translate) + ': ' + b.neu + '%'"></div>
						<div [style.width.%]="b.neg" class="bg-negative h-full rounded-full" [title]="('ANALYTICS_GLOSSARY.NEGATIVE' | translate) + ': ' + b.neg + '%'"></div>
					</div>
				</div>
				}
				@if (!loading && sentimentSplitData.length === 0) {
				<div class="caption text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_SENTIMENT_DATA' | translate }}</div>
				}
			</div>
		</div>

		<!-- Coverage Calendar Heatmap -->
		<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between">
			<div>
				<div class="flex items-center justify-between mb-1">
					<h3 class="section-title flex items-center gap-1">9. {{ 'ANALYTICS_COVERAGE_ANALYSIS.COVERAGE_CALENDAR_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.COVERAGE_CALENDAR"></app-info-icon></h3>
					<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.DAILY_INTENSITY' | translate }}</span>
				</div>
			</div>

			<div class="py-2">
				<div class="grid grid-cols-7 gap-1.5 text-center text-[10px] mb-1 font-medium text-ink-subtle">
					<span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_MON' | translate }}</span><span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_TUE' | translate }}</span><span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_WED' | translate }}</span><span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_THU' | translate }}</span><span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_FRI' | translate }}</span><span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_SAT' | translate }}</span><span>{{ 'ANALYTICS_DATE_PICKER.WEEKDAY_SUN' | translate }}</span>
				</div>
				<div class="grid grid-cols-7 gap-1.5">
					@for (blank of calendarLeadingBlanks; track $index) {
					<div></div>
					}
					@for (cd of calendarDays; track cd.date) {
					<div class="h-7 rounded-lg flex items-center justify-center text-[11px] transition-colors" [class]="getCalendarBg(cd.intensity)">
						{{ cd.day }}
					</div>
					}
				</div>
			</div>

			<div class="flex items-center justify-start caption text-ink-subtle font-normal pt-2 border-t border-line">
				<div class="flex items-center gap-1.5">
					<span>{{ 'ANALYTICS_COVERAGE_ANALYSIS.LOW_LABEL' | translate }}</span>
					<span class="w-3 h-3 rounded-sm bg-seq-2"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-4"></span>
					<span class="w-3 h-3 rounded-sm bg-seq-5"></span>
					<span>{{ 'ANALYTICS_COVERAGE_ANALYSIS.HIGH_LABEL' | translate }}</span>
				</div>
			</div>
		</div>
	</div>
	}

	@if (metric === 'Reach') {
	<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between">
		<div>
			<div class="flex items-center justify-between mb-1">
				<h3 class="section-title flex items-center gap-1">8. {{ 'ANALYTICS_COVERAGE_ANALYSIS.VISIBILITY_DISTRIBUTION_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.VISIBILITY_DISTRIBUTION_BY_TIER"></app-info-icon></h3>
				<span class="caption text-ink-subtle">{{ 'ANALYTICS_COVERAGE_ANALYSIS.PROMINENCE_TIERS' | translate }}</span>
			</div>
		</div>

		<div class="space-y-3 py-2 flex-1 flex flex-col justify-center">
			@for (tier of reachTierData; track tier.tier) {
			<div class="space-y-1">
				<div class="flex justify-between items-center text-[11px]">
					<div class="flex items-center gap-1.5">
						<span class="w-2.5 h-2.5 rounded-sm" [style.background-color]="tier.color"></span>
						<span class="font-medium text-ink">{{ tier.tier }}</span>
					</div>
					<div class="flex items-center gap-2">
						<span class="font-medium text-ink tabular-nums">{{ 'ANALYTICS_COVERAGE_ANALYSIS.ARTICLES_COUNT' | translate : { count: fmtInt(tier.mentions) } }}</span>
						<span class="text-ink-subtle text-[10px]">({{ tier.percentage }})</span>
					</div>
				</div>
				<div class="w-full bg-neutral-tint rounded-full h-2.5 overflow-hidden">
					<div class="h-full rounded-full transition-all" [style.width]="tier.percentage" [style.background-color]="tier.color"></div>
				</div>
			</div>
			}
			@if (!loading && reachTierData.length === 0) {
			<div class="caption text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_VISIBILITY_DATA' | translate }}</div>
			}
		</div>
	</div>
	}

	@if (metric === 'Impact') {
	<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between">
		<div>
			<div class="flex items-center justify-between mb-1">
				<div class="flex items-center gap-2">
					<h3 class="section-title flex items-center gap-1">8. {{ 'ANALYTICS_COVERAGE_ANALYSIS.IMPACT_VISIBILITY_SCATTER_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.COVERAGE_ANALYSIS.IMPACT_VISIBILITY_SCATTER"></app-info-icon></h3>
					<span class="bg-blue-tint text-blue text-xs font-medium px-2 py-0.5 rounded-full">
						{{ 'ANALYTICS_COVERAGE_ANALYSIS.ARTICLES_PLOTTED' | translate : { count: scatterPoints.length } }}
					</span>
				</div>
				<div class="flex items-center gap-4 caption font-medium text-ink-muted">
					<span class="flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-positive"></span> {{ 'ANALYTICS_COVERAGE_ANALYSIS.POSITIVE_IMPACT_LEGEND' | translate }}</span>
					<span class="flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-neutral-bar"></span> {{ 'ANALYTICS_COVERAGE_ANALYSIS.NEUTRAL_IMPACT_LEGEND' | translate }}</span>
					<span class="flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-negative"></span> {{ 'ANALYTICS_COVERAGE_ANALYSIS.NEGATIVE_IMPACT_LEGEND' | translate }}</span>
				</div>
			</div>
			<p class="caption text-ink-subtle font-normal mb-3">
				{{ 'ANALYTICS_COVERAGE_ANALYSIS.SCATTER_DESCRIPTION' | translate }}
			</p>
		</div>

		<div class="w-full h-[360px] relative">
			<app-chart type="scatter" [data]="scatterChartData" [options]="scatterChartOptions" height="360px"></app-chart>
		</div>
		@if (scatterPoints.length > 0) {
		<app-sample-size-note class="block mt-1" [count]="scatterPoints.length"></app-sample-size-note>
		}

		@if (!loading && scatterPoints.length === 0) {
		<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_SCATTER_DATA' | translate }}</div>
		}
	</div>
	}
	</div>
	}
</div>
